import React, { useState, useEffect } from 'react';
import './Chatbot.css';

interface Message {
    text: string;
    isBot: boolean;
    isContact?: boolean;
}

const Chatbot: React.FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [showLabel, setShowLabel] = useState(false);
    const [message, setMessage] = useState("");
    const [messages, setMessages] = useState<Message[]>([
        { text: "Hello! Welcome to Showbouys Foundation LBG. How can we help you today?", isBot: true }
    ]);

    const faqs = [
        { q: "What is Showbouys Foundation?", a: "We are a foundation dedicated to youth empowerment, sustainable agriculture, environmental stewardship, and community development." },
        { q: "How can I donate?", a: "You can donate via our Donate page or contact us directly at +233 24 966 4539 for bank details." },
        { q: "Where are you located?", a: "We are based in Ghana, working across various rural communities to drive sustainable development." },
        { q: "Talk to a Human", a: "Sure! You can reach us directly via WhatsApp at +233 24 966 4539 or email us at showbouys@gmail.com.", isContact: true }
    ];

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowLabel(true);
        }, 10000);
        return () => clearTimeout(timer);
    }, []);

    const handleSend = (e: React.FormEvent | string) => {
        let text = "";
        if (typeof e === 'string') {
            text = e;
        } else {
            e.preventDefault();
            if (!message.trim()) return;
            text = message;
            setMessage("");
        }

        const newMessages = [...messages, { text: text, isBot: false }];
        setMessages(newMessages);

        // Find if it's an FAQ
        const faq = faqs.find(f => f.q === text);
        
        setTimeout(() => {
            if (faq) {
                setMessages(prev => [...prev, { 
                    text: faq.a, 
                    isBot: true,
                    isContact: faq.isContact 
                }]);
            } else {
                setMessages(prev => [...prev, { 
                    text: "Thank you for your message! One of our team members will get back to you shortly. For urgent matters, please use the WhatsApp link below.", 
                    isBot: true,
                    isContact: true
                }]);
            }
        }, 800);
    };

    return (
        <div className="chatbot-container">
            {showLabel && !isOpen && (
                <div className="chatbot-label" onClick={() => setIsOpen(true)}>
                    Need help? Chat with us!
                </div>
            )}
            
            <button className={`chatbot-toggle ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <i className="fas fa-times"></i> : <i className="fas fa-comment-dots"></i>}
            </button>

            {isOpen && (
                <div className="chatbot-window">
                    <div className="chatbot-header">
                        <div className="bot-info">
                            <div className="bot-avatar">
                                <i className="fas fa-robot"></i>
                            </div>
                            <div className="bot-name">
                                <h3>Showbouys Assistant</h3>
                                <p>Online | Typical reply in 1h</p>
                            </div>
                        </div>
                        <button className="close-btn d-lg-none" onClick={() => setIsOpen(false)}>
                            <i className="fas fa-times"></i>
                        </button>
                    </div>
                    
                    <div className="chatbot-messages">
                        {messages.map((msg, idx) => (
                            <div key={idx} className={`message ${msg.isBot ? 'bot' : 'user'}`}>
                                <div className="message-text">
                                    {msg.text}
                                    {msg.isContact && (
                                        <div className="contact-links">
                                            <a href="https://wa.me/233249664539?text=Hello%20Showbouys%20Foundation!%20I%20am%20reaching%20out%20from%20your%20website%20and%20would%20like%20to%20know%20more." target="_blank" rel="noopener noreferrer" className="whatsapp-link">
                                                <i className="fab fa-whatsapp"></i> Chat on WhatsApp
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        
                        {messages.length === 1 && (
                            <div className="quick-replies">
                                <p>Suggested questions:</p>
                                {faqs.map((faq, idx) => (
                                    <button key={idx} onClick={() => handleSend(faq.q)}>
                                        {faq.q}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <form className="chatbot-input" onSubmit={handleSend}>
                        <input 
                            type="text" 
                            placeholder="Type your message..." 
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                        />
                        <button type="submit">
                            <i className="fas fa-paper-plane"></i>
                        </button>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Chatbot;
